import { Link, Navigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import pmManagerShot from "../assets/screenshots/website-pm-manager.jpeg";

// Old homepage anchors. Hash fragments never reach the server, so these are redirected
// client-side (replace, so the back button isn't broken). /#pricing is a live Google Ads sitelink.
const HASH_REDIRECTS = {
  "#pricing": "/pricing",
  "#idea-to-charter": "/charter",
  "#visualizer": "/tierup",
};

function Hero() {
  return (
    <section style={{ padding: "4.5rem 20px 2.5rem", textAlign: "center", maxWidth: 820, margin: "0 auto" }}>
      <div style={{ marginBottom: 18 }}>
        <Badge>Practitioner-Built Platform</Badge>
      </div>
      <h1
        style={{
          fontFamily: "var(--font-voice)",
          fontSize: "clamp(32px, 4.5vw, 48px)",
          color: "var(--navy)",
          margin: "0 0 18px",
          lineHeight: 1.2,
          fontWeight: 700,
        }}
      >
        It&rsquo;s 11pm. The Steering Committee is tomorrow.
        <br />
        Groundwork helps you walk in with a plan.
      </h1>
      <p style={{ fontSize: 16, color: "var(--text-secondary)", margin: "0 0 32px", lineHeight: 1.6 }}>
        Groundwork TierUp helps turn messy work into something you can govern, explain and move forward.
      </p>
      <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
        <Button href="https://groundworktierup.app/?intent=register" external size="lg">
          Get Started &rarr;
        </Button>
        <Button to="/calculator" variant="secondary" size="lg">
          Calculate Your Delay Cost
        </Button>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "14px 0 0" }}>
        Plans from $59 a month. Cancel anytime.
      </p>
    </section>
  );
}


// One static product glimpse. The image file is untouched: CSS crops it to the PM Manager
// workbook panel, leaving out the app's top bar and sidebar (which carry names that will
// change), and fades the bottom edge. Source is 1467x812; the visible window starts at
// x=315, y=92 and is 1110x720 (percentages below are derived from those numbers).
function ProductGlimpse() {
  return (
    <section style={{ maxWidth: 960, margin: "0 auto 4rem", padding: "0 20px" }}>
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          aspectRatio: "1110 / 720",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-strong)",
          boxShadow: "var(--shadow-lg)",
          background: "var(--surface-0)",
          WebkitMaskImage: "linear-gradient(#000 82%, transparent)",
          maskImage: "linear-gradient(#000 82%, transparent)",
        }}
      >
        <img
          src={pmManagerShot}
          alt="PM Manager workbook for a demo initiative, showing the tool tabs, the export buttons, and the workbook tabs for WBS and Gantt, timeline and dependencies, stakeholders, actions, decisions and changes, RAID register and status report."
          width={1467}
          height={812}
          loading="lazy"
          style={{ position: "absolute", width: "132.16%", height: "auto", left: "-28.38%", top: "-12.78%", display: "block", maxWidth: "none" }}
        />
      </div>
    </section>
  );
}

// Doors row: one link card per destination. Labels reuse the names already used in the nav and
// page titles. A door with an empty label isn't shown, and the row hides if all are empty.
const DOORS = [
  { to: "/charter", label: "Idea to Charter" },
  { to: "/calculator", label: "Delay Cost Calculator" },
  { to: "/tierup", label: "Product Overview" },
  { to: "/pricing", label: "Pricing" },
];

function Doors() {
  const doors = DOORS.filter((d) => d.label);
  if (doors.length === 0) return null;
  return (
    <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20, maxWidth: 960, margin: "0 auto 4rem", padding: "0 20px" }}>
      {doors.map((d) => (
        <Link key={d.to} to={d.to} className="glass-card hover-card" style={{ padding: "1.75rem", textAlign: "center", fontWeight: 600, color: "var(--navy)" }}>
          {d.label}
        </Link>
      ))}
    </section>
  );
}

function NotTheTools() {
  return (
    <section style={{ maxWidth: 720, margin: "0 auto 4rem", padding: "0 20px", textAlign: "center" }}>
      <h2 style={{ fontFamily: "var(--font-voice)", fontSize: "clamp(24px, 3.5vw, 32px)", color: "var(--navy)", margin: "0 0 20px", lineHeight: 1.25 }}>
        It&rsquo;s not the tools. It&rsquo;s the judgement.
      </h2>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", margin: "0 0 16px", lineHeight: 1.7 }}>
        AI Coach. PM Manager. RTG Planner. CRISP Assessment. Those are the capabilities you&rsquo;ll see when you look at Groundwork TierUp.
      </p>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", margin: "0 0 16px", lineHeight: 1.7 }}>
        What&rsquo;s actually inside them is twenty years of project, change, governance, and improvement judgement: the calls that usually only exist in one practitioner&rsquo;s head, built into the workflow instead.
      </p>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", margin: 0, lineHeight: 1.7 }}>
        Groundwork wasn&rsquo;t built by software developers looking for a market. It was built by a practitioner tired of re-solving the same delivery problems again and again.
      </p>
    </section>
  );
}

const PROBLEMS = [
  {
    when: "When decisions keep getting reopened",
    solve: "Track ownership and accountability, so everyone can see who decided what, and when.",
  },
  {
    when: "When stakeholders say “I thought someone else was doing that”",
    solve: "Connect stakeholders, responsibilities, communications, and change impacts from one source of truth.",
  },
  {
    when: "When projects go Amber or Red",
    solve: "Use the built-in Return to Green Planner to create a structured recovery path.",
  },
  {
    when: "When lessons disappear after go-live",
    solve: "Flag lesson candidates from closed risks and decisions, so lessons are captured, not lost.",
  },
  {
    when: "When you’re preparing for tomorrow’s Steering Committee",
    solve: "Generate executive-ready outputs without rebuilding everything manually.",
  },
];

function ProblemsSolved() {
  return (
    <section style={{ maxWidth: 960, margin: "0 auto 4rem", padding: "0 20px" }}>
      <h2 style={{ fontFamily: "var(--font-voice)", fontSize: "clamp(24px, 3.5vw, 32px)", color: "var(--navy)", margin: "0 0 32px", textAlign: "center" }}>
        The Problems Groundwork Solves
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
        {PROBLEMS.map((item) => (
          <div key={item.when} className="glass-card hover-card" style={{ padding: "1.5rem" }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: "var(--navy)", margin: "0 0 8px", lineHeight: 1.4 }}>
              {item.when}
            </p>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
              {item.solve}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}


// Copy slots for the founder line and its link text. Empty until Stage 2 fills them from
// existing wording; the block renders nothing while the line is empty.
const FOUNDER_LINE = "";
const FOUNDER_LINK_LABEL = "";

function FounderLine() {
  if (!FOUNDER_LINE) return null;
  return (
    <section style={{ maxWidth: 720, margin: "0 auto 4rem", padding: "0 20px", textAlign: "center" }}>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", margin: 0, lineHeight: 1.7 }}>
        {FOUNDER_LINE}{" "}
        {FOUNDER_LINK_LABEL && (
          <Link to="/about" style={{ color: "var(--lilac-deep)", fontWeight: 600 }}>{FOUNDER_LINK_LABEL} &rarr;</Link>
        )}
      </p>
    </section>
  );
}

// Copy slot for the Coming Soon teaser link. Empty until /coming-soon has real content and the
// teaser is written (Stage 2); the block renders nothing while it is empty.
const TEASER_LABEL = "";

function ComingSoonTeaser() {
  if (!TEASER_LABEL) return null;
  return (
    <section style={{ maxWidth: 720, margin: "0 auto 4rem", padding: "0 20px", textAlign: "center" }}>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", margin: 0, lineHeight: 1.7 }}>
        <Link to="/coming-soon" style={{ color: "var(--lilac-deep)", fontWeight: 600 }}>{TEASER_LABEL} &rarr;</Link>
      </p>
    </section>
  );
}

function FinalCTA() {
  return (
    <section
      style={{
        margin: "0 20px 4rem",
        maxWidth: 820,
        marginLeft: "auto",
        marginRight: "auto",
        background: "var(--lilac-wash)",
        borderRadius: "var(--radius-lg)",
        padding: "3rem 2rem",
        textAlign: "center",
      }}
    >
      <h3 style={{ fontFamily: "var(--font-voice)", fontSize: 24, color: "var(--navy)", margin: "0 0 12px" }}>
        Ready to stop relitigating the same decision every Tuesday?
      </h3>
      <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 24px" }}>
        Groundwork TierUp is live. Register and get started today.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
        <Button href="https://groundworktierup.app/?intent=register" external>
          Start using Groundwork TierUp
        </Button>
        <Button to="/charter" variant="secondary">
          See what Groundwork produces
        </Button>
        <a href="https://groundwork-tierup.kit.com" target="_blank" rel="noopener noreferrer" style={{ color: "var(--lilac-deep)", fontWeight: 600, fontSize: 14 }}>
          Learn how it works
        </a>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "14px 0 0" }}>
        Plans from $59 a month. Cancel anytime.
      </p>
    </section>
  );
}

// Reassurance line above the final call to action. Every item is stated in the Privacy Policy,
// Terms of Service or Cancellation Policy.
function TrustStrip() {
  return (
    <section style={{ maxWidth: 820, margin: "0 auto 1.5rem", padding: "0 20px", textAlign: "center" }}>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 8px", lineHeight: 1.7 }}>
        Project data hosted in Australia &middot; You own your work &middot; Payments handled by Stripe &middot; Cancel anytime
      </p>
      <p style={{ fontSize: 13, margin: 0 }}>
        <Link to="/privacy" style={{ color: "var(--lilac-deep)", fontWeight: 600 }}>
          How Groundwork TierUp handles your information &rarr;
        </Link>
      </p>
    </section>
  );
}


export default function Home() {
  const { hash } = useLocation();
  usePageTitle("Project, Change & BI Platform");
  if (HASH_REDIRECTS[hash]) return <Navigate to={HASH_REDIRECTS[hash]} replace />;
  return (
    <>
      <Hero />
      <ProductGlimpse />
      <ProblemsSolved />
      <Doors />
      <NotTheTools />
      <FounderLine />
      <ComingSoonTeaser />
      <TrustStrip />
      <FinalCTA />
    </>
  );
}
